/** @jsxImportSource @emotion/react */
import { useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { IconButton } from "@mui/material";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import { FavoriteOutlined } from "@mui/icons-material";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import StoreMapModal from "./StoreMapModal";

// Flat store/restaurant illustration — awning + display windows + door
export const StoreFrontIllustration = ({ size = 72 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 72 72"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect x="8" y="28" width="56" height="34" rx="2" fill="#FFF3E0" />
    <rect x="6" y="20" width="60" height="14" rx="3" fill="#54ac34" />
    <line x1="16" y1="20" x2="12" y2="34" stroke="#3d8a27" strokeWidth="3" />
    <line x1="26" y1="20" x2="22" y2="34" stroke="#3d8a27" strokeWidth="3" />
    <line x1="36" y1="20" x2="32" y2="34" stroke="#3d8a27" strokeWidth="3" />
    <line x1="46" y1="20" x2="42" y2="34" stroke="#3d8a27" strokeWidth="3" />
    <line x1="56" y1="20" x2="52" y2="34" stroke="#3d8a27" strokeWidth="3" />
    <path
      d="M6 33 Q11 38 16 33 Q21 38 26 33 Q31 38 36 33 Q41 38 46 33 Q51 38 56 33 Q61 38 66 33"
      stroke="#54ac34"
      strokeWidth="2.5"
      fill="none"
      strokeLinecap="round"
    />
    <rect
      x="12"
      y="36"
      width="18"
      height="14"
      rx="2"
      fill="#B3E5FC"
      stroke="#e0e0e0"
      strokeWidth="1"
    />
    <line x1="21" y1="36" x2="21" y2="50" stroke="#fff" strokeWidth="1.5" />
    <line x1="12" y1="43" x2="30" y2="43" stroke="#fff" strokeWidth="1.5" />
    <rect
      x="42"
      y="36"
      width="18"
      height="14"
      rx="2"
      fill="#B3E5FC"
      stroke="#e0e0e0"
      strokeWidth="1"
    />
    <line x1="51" y1="36" x2="51" y2="50" stroke="#fff" strokeWidth="1.5" />
    <line x1="42" y1="43" x2="60" y2="43" stroke="#fff" strokeWidth="1.5" />
    <rect
      x="29"
      y="42"
      width="14"
      height="20"
      rx="2"
      fill="#A5D6A7"
      stroke="#81C784"
      strokeWidth="1"
    />
    <circle cx="40" cy="52" r="1.5" fill="#4CAF50" />
    <rect x="4" y="17" width="64" height="5" rx="2" fill="#3d8a27" />
    <rect x="18" y="8" width="36" height="11" rx="3" fill="#FF7043" />
    <rect x="20" y="10" width="32" height="7" rx="2" fill="#FF8A65" />
  </svg>
);

// Smaller footprint for the home page's mobile layout, where this card sits
// right under the category chips rather than in its own dedicated search
// tab — applied as css-prop overrides on top of the shared `classes` below
// so the search drawer's own (larger) presentation is untouched.
const COMPACT_CARD_WIDTH = "104px";
const COMPACT = {
  // classes.brandCard hardcodes width: "165px" — without this override the
  // inner card stayed full-size while only its outer wrapper shrank, so it
  // visibly spilled into the next card's space instead of actually being
  // narrower.
  brandCard: { width: COMPACT_CARD_WIDTH },
  storeHeader: { padding: "10px 8px 32px" },
  storeImageArea: { width: "72px", height: "72px", marginTop: "-36px", border: "2px solid #fff" },
  storeCardBody: { padding: "5px 8px 10px", gap: "3px" },
  storeCardBodyName: { fontSize: "0.72rem" },
  storeCardActions: { gap: "4px", marginTop: "2px" },
};

// Shared store card — used by the home page brand carousel and the search
// overlay's "Brands" tab, so both present the exact same design.
const StoreCard = ({
  item,
  selBrand,
  classes,
  onBrandSelect,
  onFavouriteToggle,
  onAfterSelect,
  compact = false,
}) => {
  const [isFav, setIsFav] = useState(item?.is_faviourate === "true");
  const [mapOpen, setMapOpen] = useState(false);
  const isActive =
    selBrand?.indexOf(item.uuid) !== -1 || item.is_highlight === "true";

  const handleCardClick = () => {
    onBrandSelect(!isActive, item.uuid, item);
    onAfterSelect?.();
  };

  const handleFavClick = (e) => {
    e.stopPropagation();
    setIsFav((prev) => !prev);
    onFavouriteToggle(e, item.uuid);
  };

  return (
    <>
      <div
        style={{
          position: "relative",
          flexShrink: 0,
          margin: compact ? "4px" : "6px",
          width: compact ? COMPACT_CARD_WIDTH : "165px",
        }}
      >
        {item.mails_count > 0 && (
          <Tooltip
            title={`${item.mails_count} New Mail${item.mails_count !== 1 ? "s" : ""}`}
            arrow
          >
            <div
              css={classes.storeMailBadge}
              onClick={(e) => {
                e.stopPropagation();
                setMapOpen(true);
              }}
            >
              {item.mails_count}
            </div>
          </Tooltip>
        )}
        <div
          css={[classes.brandCard, compact && COMPACT.brandCard]}
          style={
            isActive
              ? {
                  border: "2.5px solid #54ac34",
                  boxShadow: "0 0 10px rgba(84,172,52,0.35)",
                }
              : {}
          }
          onClick={handleCardClick}
        >
          <div css={[classes.storeHeader, compact && COMPACT.storeHeader]}>
            <IconButton
              css={classes.storeFavBtn}
              onClick={handleFavClick}
              size="small"
            >
              {isFav ? (
                <FavoriteOutlined
                  sx={{ color: "red", fontSize: compact ? "0.85rem" : "1rem" }}
                />
              ) : (
                <FavoriteBorderOutlinedIcon
                  sx={{
                    color: "rgba(255,255,255,0.85)",
                    fontSize: compact ? "0.85rem" : "1rem",
                  }}
                />
              )}
            </IconButton>
          </div>

          <div
            css={[classes.storeImageArea, compact && COMPACT.storeImageArea]}
          >
            {item.logo_url ? (
              <img
                src={item.logo_url}
                alt={item.company_name}
                css={classes.storeImg}
              />
            ) : (
              <StoreFrontIllustration size={compact ? 42 : 72} />
            )}
          </div>

          <div css={[classes.storeCardBody, compact && COMPACT.storeCardBody]}>
            <span
              css={[
                classes.storeCardBodyName,
                compact && COMPACT.storeCardBodyName,
              ]}
            >
              {item.company_name}
            </span>
            <div
              css={[
                classes.storeCardActions,
                compact && COMPACT.storeCardActions,
              ]}
            >
              {item.mobile && (
                <Tooltip title={`Call ${item.mobile}`} arrow placement="bottom">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      window.location.href = `tel:${item.mobile}`;
                    }}
                    sx={{
                      background: "rgba(84,172,52,0.1)",
                      "&:hover": { background: "rgba(84,172,52,0.22)" },
                    }}
                  >
                    <PhoneAndroidIcon
                      sx={{ fontSize: "1rem", color: "#54ac34" }}
                    />
                  </IconButton>
                </Tooltip>
              )}
              {item.address1 && (
                <Tooltip title="View on map" arrow placement="bottom">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setMapOpen(true);
                    }}
                    sx={{
                      background: "rgba(84,172,52,0.1)",
                      "&:hover": { background: "rgba(84,172,52,0.22)" },
                    }}
                  >
                    <LocationOnOutlinedIcon
                      sx={{ fontSize: "1rem", color: "#54ac34" }}
                    />
                  </IconButton>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div>
      {mapOpen && (
        <StoreMapModal store={item} onClose={() => setMapOpen(false)} />
      )}
    </>
  );
};

export default StoreCard;
